/**
 * Write Adapter — converts HUD diffs to existing WS commands.
 *
 * Symmetric pair with hud-adapter.ts:
 *   fromSheet()  — server → HUD (read)
 *   toCommands()  — HUD → server (write)
 *
 * Pure function, no side effects.
 *
 * T002 NOTE: Signature intentionally NOT changed in this refactor —
 * `RulesCharacterData` here is the target shape for the write direction
 * (CharacterHudOverlay.tsx:218 passes a snapshot through this function).
 * Frontend `HudCharacterData` is structurally assignable to
 * `RulesCharacterData` (see structural contract in ../rules/index.ts and
 * src/lib/character-hud-store.ts), so callers no longer need
 * `as unknown as RulesCharacterData` casts.
 */

import type { RulesCharacterData } from '../rules/index.js';
import type { RestType } from '../../../utils.js';

// ─── DeepPartial (inline — shared/ can't import src/) ────────

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

// ─── Local HudResource alias (shared/ can't import src/) ─────
type HudResource = { id: string; name: string; current: number; max: number; recharge: 'short' | 'long' | 'dawn' };

// Local-widened slot view: the canonical RulesCharacterData['spells']['slots']
// value type does not declare `pact`, but hud-adapter.ts/mapSpells emits it
// for warlock pact-magic slots. We read through this view so we can route
// slot-spend / slot-restore commands to `level: 'pact'` when appropriate
// (the SheetCommand union already accepts `number | 'pact'`).
type HudSlotView = { max: number; current: number; pact?: boolean };

// ─── Spellbook update changes shape (T004 extension) ──────────
type SpellbookComponents = { v?: boolean; s?: boolean; m?: boolean; materialDesc?: string };
type SpellbookUpdateChanges = {
  state?: string;
  name?: string;
  level?: number;
  school?: string;
  components?: SpellbookComponents;
  ritual?: boolean;
  concentration?: boolean;
  description?: string;
  higherLevels?: string;
  actionType?: string;
};

// ─── SheetCommand Union Type ─────────────────────────────────

export type SheetCommand =
  | { type: 'sheet:update'; sheetId: string; changes: Record<string, unknown> }
  | { type: 'sheet:apply-damage'; sheetId: string; amount: number }
  | { type: 'sheet:apply-healing'; sheetId: string; amount: number }
  | { type: 'sheet:spell-slot-spend'; sheetId: string; level: number | 'pact' }
  | { type: 'sheet:spell-slot-restore'; sheetId: string; level: number | 'pact' }
  | { type: 'sheet:movement-spend'; sheetId: string; delta: number }
  | { type: 'sheet:concentration-set'; sheetId: string; spellSlug: string | null }
  | { type: 'sheet:ability-use'; sheetId: string; abilityId: string }
  | { type: 'sheet:ability-restore'; sheetId: string; abilityId: string; restoreBy?: number }
  | { type: 'sheet:ability-add'; sheetId: string; ability: { name: string; description: string; maxUses: number | null; restType: RestType; source?: string } }
  | { type: 'sheet:ability-remove'; sheetId: string; abilityId: string }
  | { type: 'sheet:ability-edit'; sheetId: string; featureId: string; patch: Partial<{ name: string; description: string; source: string; uses: Partial<{ max: number; recharge: 'short' | 'long' | 'dawn' }> }> }
  | { type: 'sheet:effect-add'; sheetId: string; effect: { source: { name: string }; target: string; mode: string; value: number; priority?: number; disabled?: boolean } }
  | { type: 'sheet:effect-update'; sheetId: string; effectId: string; changes: Record<string, unknown> }
  | { type: 'sheet:effect-remove'; sheetId: string; effectId: string }
  | { type: 'sheet:repeating-row-add'; sheetId: string; sectionKey: string; row: { rowId: string; values: Record<string, unknown> } }
  | { type: 'sheet:repeating-row-update'; sheetId: string; sectionKey: string; rowId: string; changes: Record<string, unknown> }
  | { type: 'sheet:repeating-row-remove'; sheetId: string; sectionKey: string; rowId: string }
  | { type: 'sheet:death-save-roll'; sheetId: string }
  | { type: 'sheet:death-save-clear'; sheetId: string }
  | { type: 'sheet:hit-dice-spend'; sheetId: string }
  | { type: 'sheet:hit-dice-restore'; sheetId: string; restoreBy?: number }
  | { type: 'sheet:resource-add'; sheetId: string; resource: { name: string; current: number; max: number; recharge: 'short' | 'long' | 'dawn' } }
  | { type: 'sheet:resource-edit'; sheetId: string; resourceId: string; changes: Partial<{ name: string; current: number; max: number; recharge: 'short' | 'long' | 'dawn' }> }
  | { type: 'sheet:resource-remove'; sheetId: string; resourceId: string }
  | { type: 'sheet:rest'; sheetId: string; restType: 'short' | 'long' }
  | { type: 'sheet:level-up'; sheetId: string; choices: { hpMethod: string; asiChoices?: unknown; newClassSlug?: string } }
  | { type: 'sheet:spellbook-add'; sheetId: string; entry: { spellSlug: string; name: string; level: number; state: string; source: string; school?: string; components?: SpellbookComponents; ritual?: boolean; concentration?: boolean; actionType?: string } }
  | { type: 'sheet:spellbook-remove'; sheetId: string; spellSlug: string }
  | { type: 'sheet:spellbook-update'; sheetId: string; spellSlug: string; changes: SpellbookUpdateChanges }
  | { type: 'sheet:set-portrait'; sheetId: string; portrait: string | null }
  // Roleplay (rp) — granular write bridge for the full-sheet overlay. The
  // singleton-store mount had these emitters; the overlay (adapter store) did
  // not, so RP edits produced no command and the optimistic overlay timed out.
  | { type: 'sheet:rp-field-update'; sheetId: string; field: 'ideal' | 'bond' | 'flaw' | 'backstory' | 'manners'; value: string }
  | { type: 'sheet:rp-list-add'; sheetId: string; listKey: 'goals' | 'npcs' | 'factions' | 'sessionLog' | 'moments'; item: Record<string, unknown> }
  | { type: 'sheet:rp-list-update'; sheetId: string; listKey: 'goals' | 'npcs' | 'factions' | 'sessionLog' | 'moments'; itemId: string; changes: Record<string, unknown> }
  | { type: 'sheet:rp-list-remove'; sheetId: string; listKey: 'goals' | 'npcs' | 'factions' | 'sessionLog' | 'moments'; itemId: string }
  | BoardConfigUpdateCommand;

// ─── Write Key Map ───────────────────────────────────────────

export const WRITE_KEY_MAP: Record<string, string> = {
  'identity.name': 'name',
  'identity.race': 'race',
  'identity.className': 'class_name',
  'identity.alignment': 'alignment',
  'identity.level': 'level',
  'identity.subrace': 'subrace',
  'identity.subclass': 'subclass',
  'identity.gender': 'gender',
  'identity.height': 'height',
  'identity.weight': 'weight',
  'identity.age': 'age',
  'identity.background': 'background',
  // Roleplay "Who I Am" (HeroCard) freeform flavor. Were unrouted → dropped by
  // the generic fallback → the optimistic leaves never confirmed → 5s
  // write-timeout reverted the card and toasted "Server did not respond".
  'identity.physicalDescription': 'physical_description',
  'identity.appearance': 'appearance',
  'identity.quote': 'quote',
  'stats.str': 'str',
  'stats.dex': 'dex',
  'stats.con': 'con',
  'stats.int': 'int',
  'stats.wis': 'wis',
  'stats.cha': 'cha',
  'saves.str': 'str_save_prof',
  'saves.dex': 'dex_save_prof',
  'saves.con': 'con_save_prof',
  'saves.int': 'int_save_prof',
  'saves.wis': 'wis_save_prof',
  'saves.cha': 'cha_save_prof',
  'skills.acrobatics': 'acrobatics_prof',
  'skills.animalHandling': 'animal_handling_prof',
  'skills.arcana': 'arcana_prof',
  'skills.athletics': 'athletics_prof',
  'skills.deception': 'deception_prof',
  'skills.history': 'history_prof',
  'skills.insight': 'insight_prof',
  'skills.intimidation': 'intimidation_prof',
  'skills.investigation': 'investigation_prof',
  'skills.medicine': 'medicine_prof',
  'skills.nature': 'nature_prof',
  'skills.perception': 'perception_prof',
  'skills.performance': 'performance_prof',
  'skills.persuasion': 'persuasion_prof',
  'skills.religion': 'religion_prof',
  'skills.sleightOfHand': 'sleight_of_hand_prof',
  'skills.stealth': 'stealth_prof',
  'skills.survival': 'survival_prof',
  'currency.cp': 'cp',
  'currency.sp': 'sp',
  'currency.ep': 'ep',
  'currency.gp': 'gp',
  'currency.pp': 'pp',
  // 2026-06-01: aligned с hud-adapter READ keys (max_hp/temp_hp). Previously
  // mapped к hp_max/hp_temp which adapter never read → max-HP changes round-tripped
  // server-side но UI снова показывал старое значение.
  'hp.max': 'max_hp',
  'hp.temp': 'temp_hp',
  // combat.initiative deliberately has NO write route (removed 2026-07-03,
  // batch A): `initiative` is a template formula field (dex_mod +
  // initiative_bonus) and recalcComputedFields overwrites any direct write —
  // the route existed but its effect was silently zero. Edit the bonus below.
  // Wave 6 (2026-06-02) — hybrid bonus pattern. Editing the bonus persists
  // to `values.initiative_bonus`; server's recalcComputedFields evaluates
  // formula `dex_mod + initiative_bonus` and overwrites `values.initiative`.
  'combat.initiativeBonus': 'initiative_bonus',
  'combat.round': 'round',
  // NOTE: combat.movementSpent is NOT here — it routes through the relative
  // sheet:movement-spend command (see the dedicated block below). An absolute
  // sheet:update write raced the map-drag writer (token.ts accumulates feet on
  // the bearer's turn) and silently overwrote its accumulation.
  'combat.notes': 'combat_notes',
  'proficiencies': 'proficiencies',
  'spells.stat': 'spellcasting_ability',
  'speed': 'speed',
  'xp': 'xp',
  'inspiration': 'inspiration',
  // 2026-06-02: removed duplicate 'combat_notes' → 'combat_notes' (dead — нет ни одного
  // callsite которого emit'ит этот path; canonical 'combat.notes' уже на line 140).
  'ac': 'ac',
  // #2 AC — the manual «прочий бонус» piece. Editing it persists to
  // values.misc_ac; the server's recomputeDnd5eAc folds it into the AC total
  // and broadcasts the new `ac`. (The `ac` total itself is no longer edited
  // directly from the Defenses panel — the stepper drives miscAc.)
  'miscAc': 'misc_ac',
  // #2 AC — manual armor bonus + shield toggle (for chars who don't track SRD
  // armor as inventory). Server's recomputeDnd5eAc folds both into the AC total.
  'armorAc': 'armor_ac',
  'shieldOn': 'shield_on',
  // Proficiency category persistence — parallel list to the flat `proficiencies`,
  // each entry "name<US>category" so the user's chosen category survives instead
  // of being re-inferred by keyword. See ProficienciesSection.
  'proficiencyCats': 'proficiency_cats',
  // Wave 6 — bonus piece for passive perception hybrid formula.
  'passivePerceptionBonus': 'passive_perception_bonus',
  // Editable movement speeds (walk is `speed` above). Flat top-level keys.
  'speedClimb': 'speed_climb',
  'speedSwim': 'speed_swim',
  'speedFly': 'speed_fly',
  // Editable senses (nested under `senses` — see flattenPaths whitelist).
  'senses.darkvision': 'darkvision',
  'senses.blindsight': 'blindsight',
  'senses.tremorsense': 'tremorsense',
  'senses.truesight': 'truesight',
  // Damage modifiers — top-level string[] lists. The generic fallback passes
  // arrays through (it only skips non-array objects), so editing these in the
  // Defenses panel persists via sheet:update { damage_*: [...] } with set
  // (full-array) semantics.
  'resistances': 'damage_resistances',
  'immunities': 'damage_immunities',
  'vulnerabilities': 'damage_vulnerabilities',
};

function flattenPaths(
  obj: Record<string, unknown>,
  prefix = '',
): Array<[string, unknown]> {
  const entries: Array<[string, unknown]> = [];
  for (const key of Object.keys(obj)) {
    const val = obj[key];
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (
      typeof val === 'object' && val !== null && !Array.isArray(val) &&
      WRITE_KEY_MAP[fullKey] === undefined &&
      ['identity', 'stats', 'saves', 'skills', 'currency', 'hp', 'combat', 'spells', 'senses'].includes(prefix ? prefix : key)
    ) {
      entries.push(...flattenPaths(val as Record<string, unknown>, fullKey));
    } else {
      entries.push([fullKey, val]);
    }
  }
  return entries;
}

function componentsEqual(a: SpellbookComponents | undefined, b: SpellbookComponents | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    (a.v ?? false) === (b.v ?? false) &&
    (a.s ?? false) === (b.s ?? false) &&
    (a.m ?? false) === (b.m ?? false) &&
    (a.materialDesc ?? undefined) === (b.materialDesc ?? undefined)
  );
}

export function toCommands(
  diff: DeepPartial<RulesCharacterData>,
  currentData: RulesCharacterData,
  sheetId: string,
): SheetCommand[] {
  const commands: SheetCommand[] = [];
  const handledKeys = new Set<string>();
  const handledPaths = new Set<string>();

  // 1. HP
  // 2026-06-02: было `handledKeys.add('hp')` что блокировало generic-fallback
  // для hp.max и hp.temp когда batched diff содержал и current. Заменено на
  // narrower handledPaths.add('hp.current') — max/temp паттерн теперь проходят
  // через generic fallback с правильными ключами (max_hp/temp_hp).
  if (diff.hp?.current !== undefined) {
    handledPaths.add('hp.current');
    const newHp = diff.hp.current as number;
    const oldHp = currentData.hp.current;
    if (newHp < oldHp) {
      // Damage: fold in any simultaneous temp-HP drop. The optimistic ± HP
      // prediction absorbs into temp first (shared damage model), so a damage
      // click can reduce temp AND current together; the command's `amount` is
      // the TOTAL (hp loss + temp absorbed). The server re-derives the split.
      const oldTemp = currentData.hp.temp;
      const newTemp = diff.hp?.temp !== undefined ? (diff.hp.temp as number) : oldTemp;
      const tempDrop = newTemp < oldTemp ? oldTemp - newTemp : 0;
      if (tempDrop > 0) handledPaths.add('hp.temp');
      commands.push({ type: 'sheet:apply-damage', sheetId, amount: (oldHp - newHp) + tempDrop });
    } else if (newHp > oldHp) {
      commands.push({ type: 'sheet:apply-healing', sheetId, amount: newHp - oldHp });
    }
  }

  // 2. Spell slots — pact-aware (T207). The diff value type is widened
  // locally to carry the optional `pact` flag emitted by hud-adapter's
  // mapSpells; when the slot (either in the incoming diff or in the
  // current snapshot) is flagged pact, the wire command routes to
  // `level: 'pact'` instead of the numeric level. This preserves the
  // existing multi-class regular-slot path untouched.
  if (diff.spells?.slots) {
    // 2026-06-02: было handledKeys.add('spells') — блокировало spells.stat и
    // spells.concentrationSpellId. Narrower handledPaths теперь.
    handledPaths.add('spells.slots');
    const slots = diff.spells.slots as Record<number, { current?: number; pact?: boolean }>;
    const currentSlots = currentData.spells.slots as Record<number, HudSlotView>;
    for (const [lvlStr, slotDiff] of Object.entries(slots)) {
      const level = Number(lvlStr);
      if (slotDiff?.current !== undefined) {
        const currentSlot = currentSlots[level];
        if (!currentSlot) continue;
        const isPact = currentSlot.pact === true || slotDiff.pact === true;
        const wireLevel: number | 'pact' = isPact ? 'pact' : level;
        // Emit one wire command PER unit of delta, not per direction: the
        // overlay debounces patches (300ms), so two quick pip clicks coalesce
        // into a single diff (3→1). A direction-only emit sent ONE spend for
        // that diff — the server spent 1 of 2 and the client's pending patch
        // never reconciled (false «write timed out» + pip rollback).
        const delta = slotDiff.current - currentSlot.current;
        const type = delta < 0 ? 'sheet:spell-slot-spend' : 'sheet:spell-slot-restore';
        for (let i = 0; i < Math.abs(delta); i++) {
          commands.push({ type, sheetId, level: wireLevel });
        }
      }
    }
  }

  // 2b. Turn movement — RELATIVE command. Two writers touch
  // values.movement_spent (this HUD tile and token.ts map-drag accumulation);
  // an absolute sheet:update from a debounced snapshot lost whichever write
  // landed first. The server adds the delta to its authoritative value.
  if (diff.combat && 'movementSpent' in diff.combat && typeof (diff.combat as { movementSpent?: unknown }).movementSpent === 'number') {
    handledPaths.add('combat.movementSpent');
    const target = (diff.combat as { movementSpent: number }).movementSpent;
    const current = typeof currentData.combat?.movementSpent === 'number' ? currentData.combat.movementSpent : 0;
    const delta = Math.round(target - current);
    if (delta !== 0) {
      commands.push({ type: 'sheet:movement-spend', sheetId, delta });
    }
  }

  // 3. Concentration
  if (diff.spells && 'concentrationSpellId' in diff.spells) {
    handledPaths.add('spells.concentrationSpellId');
    commands.push({
      type: 'sheet:concentration-set',
      sheetId,
      spellSlug: diff.spells.concentrationSpellId as string | null,
    });
  }

  // 4. Features
  if (diff.features) {
    handledKeys.add('features');
    const diffFeatures = diff.features as Array<{
      id: string;
      name?: string;
      description?: string;
      source?: string;
      uses?: { current?: number; max?: number; recharge?: string };
    }>;
    const currentFeats = currentData.features as Array<{
      id: string;
      name: string;
      source: string;
      description: string;
      uses?: { current: number; max: number; recharge: string };
    }>;
    const currentIds = new Set(currentFeats.map((f: { id: string }) => f.id));
    const diffIds = new Set(diffFeatures.map((f: { id: string }) => f.id));

    for (const cur of currentFeats) {
      if (!diffIds.has(cur.id)) {
        commands.push({ type: 'sheet:ability-remove', sheetId, abilityId: cur.id });
      }
    }

    for (const diffFeat of diffFeatures) {
      if (!currentIds.has(diffFeat.id)) {
        commands.push({
          type: 'sheet:ability-add',
          sheetId,
          ability: {
            name: diffFeat.name ?? '',
            description: diffFeat.description ?? '',
            maxUses: diffFeat.uses?.max ?? null,
            restType: (diffFeat.uses?.recharge as RestType) ?? 'none',
            source: diffFeat.source,
          },
        });
      }
    }

    // Field edits on existing features (id preserved) — emit sheet:ability-edit
    // with patch containing only changed fields. Does NOT replace the uses.current
    // delta logic below; both can fire for the same id when both kinds of change
    // are present in a single diff.
    for (const diffFeat of diffFeatures) {
      if (!currentIds.has(diffFeat.id)) continue;
      const currentFeat = currentFeats.find((f: { id: string }) => f.id === diffFeat.id)!;
      const patch: { name?: string; description?: string; source?: string; uses?: { max?: number; recharge?: 'short' | 'long' | 'dawn' } } = {};
      if (diffFeat.name !== undefined && diffFeat.name !== currentFeat.name) patch.name = diffFeat.name;
      if (diffFeat.description !== undefined && diffFeat.description !== currentFeat.description) patch.description = diffFeat.description;
      if (diffFeat.source !== undefined && diffFeat.source !== currentFeat.source) patch.source = diffFeat.source;
      if (diffFeat.uses) {
        const usesPatch: { max?: number; recharge?: 'short' | 'long' | 'dawn' } = {};
        if (diffFeat.uses.max !== undefined && diffFeat.uses.max !== currentFeat.uses?.max) usesPatch.max = diffFeat.uses.max;
        if (diffFeat.uses.recharge !== undefined && diffFeat.uses.recharge !== currentFeat.uses?.recharge) usesPatch.recharge = diffFeat.uses.recharge as 'short' | 'long' | 'dawn';
        if (Object.keys(usesPatch).length > 0) patch.uses = usesPatch;
      }
      if (Object.keys(patch).length > 0) {
        commands.push({ type: 'sheet:ability-edit', sheetId, featureId: diffFeat.id, patch });
      }
    }

    // uses.current delta — preserved verbatim
    for (const diffFeat of diffFeatures) {
      if (!currentIds.has(diffFeat.id)) continue;
      if (diffFeat.uses?.current === undefined) continue;
      const currentFeat = currentFeats.find((f: { id: string }) => f.id === diffFeat.id);
      if (currentFeat?.uses) {
        const delta = diffFeat.uses.current - currentFeat.uses.current;
        if (delta < 0) {
          commands.push({ type: 'sheet:ability-use', sheetId, abilityId: diffFeat.id });
        } else if (delta > 0) {
          commands.push({ type: 'sheet:ability-restore', sheetId, abilityId: diffFeat.id, restoreBy: delta });
        }
      }
    }
  }

  // 4b. Spellbook
  if (diff.spells?.known) {
    const diffKnown = diff.spells.known as Array<{
      id: string;
      spellSlug?: string;
      name?: string;
      level?: number;
      state?: string;
      source?: string;
      school?: string;
      components?: SpellbookComponents;
      ritual?: boolean;
      concentration?: boolean;
      description?: string;
      higherLevels?: string;
      actionType?: string;
    }>;
    const currentKnown = currentData.spells.known as Array<{
      id: string;
      spellSlug?: string;
      name: string;
      level: number;
      state: string;
      school?: string;
      components?: SpellbookComponents;
      ritual?: boolean;
      concentration?: boolean;
      description?: string;
      higherLevels?: string;
      actionType?: string;
    }>;
    const diffSpellIds = new Set(diffKnown.map((s: { id: string }) => s.id));
    const currentSpellIds = new Set(currentKnown.map((s: { id: string }) => s.id));

    for (const cur of currentKnown) {
      if (!diffSpellIds.has(cur.id)) {
        commands.push({
          type: 'sheet:spellbook-remove',
          sheetId,
          spellSlug: cur.spellSlug ?? cur.id,
        });
      }
    }

    for (const diffSpell of diffKnown) {
      if (!currentSpellIds.has(diffSpell.id)) {
        commands.push({
          type: 'sheet:spellbook-add',
          sheetId,
          entry: {
            spellSlug: diffSpell.spellSlug ?? diffSpell.id,
            name: diffSpell.name ?? '',
            level: diffSpell.level ?? 0,
            state: diffSpell.state ?? 'known',
            source: diffSpell.source ?? '',
            school: diffSpell.school,
            components: diffSpell.components,
            ritual: diffSpell.ritual,
            concentration: diffSpell.concentration,
            actionType: diffSpell.actionType,
          },
        });
      } else {
        const currentSpell = currentKnown.find((s: { id: string }) => s.id === diffSpell.id)!;
        const changes: SpellbookUpdateChanges = {};
        if (diffSpell.state !== undefined && diffSpell.state !== currentSpell.state) {
          changes.state = diffSpell.state;
        }
        if (diffSpell.name !== undefined && diffSpell.name !== currentSpell.name) {
          changes.name = diffSpell.name;
        }
        if (diffSpell.level !== undefined && diffSpell.level !== currentSpell.level) {
          changes.level = diffSpell.level;
        }
        if (diffSpell.school !== undefined && diffSpell.school !== currentSpell.school) {
          changes.school = diffSpell.school;
        }
        if (diffSpell.ritual !== undefined && (diffSpell.ritual ?? false) !== (currentSpell.ritual ?? false)) {
          changes.ritual = diffSpell.ritual;
        }
        if (diffSpell.concentration !== undefined && (diffSpell.concentration ?? false) !== (currentSpell.concentration ?? false)) {
          changes.concentration = diffSpell.concentration;
        }
        if (diffSpell.components !== undefined && !componentsEqual(diffSpell.components, currentSpell.components)) {
          changes.components = diffSpell.components;
        }
        if (diffSpell.description !== undefined && diffSpell.description !== currentSpell.description) {
          changes.description = diffSpell.description;
        }
        if (diffSpell.higherLevels !== undefined && diffSpell.higherLevels !== currentSpell.higherLevels) {
          changes.higherLevels = diffSpell.higherLevels;
        }
        if (diffSpell.actionType !== undefined && diffSpell.actionType !== currentSpell.actionType) {
          changes.actionType = diffSpell.actionType;
        }
        if (Object.keys(changes).length > 0) {
          commands.push({
            type: 'sheet:spellbook-update',
            sheetId,
            spellSlug: diffSpell.spellSlug ?? diffSpell.id,
            changes,
          });
        }
      }
    }
  }

  // 5. Death saves — Wave 7 (2026-06-02): event-driven only.
  // Previous diff-based detection (any diff.deathSaves → emit roll, both zero → emit
  // clear) was architecturally inverted: server rolls a d20 and decides the new state,
  // so the user's optimistic counter change carried no real intent and just trigger'ed
  // a server-side roll. UI now emits sheet:death-save-roll / sheet:death-save-clear
  // directly via send() (DefensesSection buttons). We still mark `deathSaves` as
  // handledKey so any legacy caller that patches the field gets silent no-op
  // instead of accidentally triggering a roll on long-rest reset / hydration drift.
  if (diff.deathSaves !== undefined) {
    handledKeys.add('deathSaves');
  }

  // 6. Hit dice
  if (diff.hitDice?.used !== undefined) {
    handledKeys.add('hitDice');
    const newUsed = diff.hitDice.used as number;
    const oldUsed = currentData.hitDice.used;
    if (newUsed > oldUsed) {
      commands.push({ type: 'sheet:hit-dice-spend', sheetId });
    } else if (newUsed < oldUsed) {
      commands.push({ type: 'sheet:hit-dice-restore', sheetId, restoreBy: oldUsed - newUsed });
    }
  }

  // 7. Active effects
  if (diff.activeEffects) {
    handledKeys.add('activeEffects');
    const diffEffects = diff.activeEffects as Array<{ id: string; name: string; source: string; duration?: string; description?: string }>;
    const currentIds = new Set(currentData.activeEffects.map((e: { id: string }) => e.id));
    const diffIds = new Set(diffEffects.map((e: { id: string }) => e.id));

    for (const eff of diffEffects) {
      if (!currentIds.has(eff.id)) {
        commands.push({
          type: 'sheet:effect-add',
          sheetId,
          effect: {
            source: { name: eff.source },
            target: 'self',
            mode: 'add',
            value: 0,
          },
        });
      }
    }

    for (const eff of currentData.activeEffects) {
      if (!diffIds.has(eff.id)) {
        commands.push({ type: 'sheet:effect-remove', sheetId, effectId: eff.id });
      }
    }

    for (const eff of diffEffects) {
      if (currentIds.has(eff.id)) {
        const currentEff = currentData.activeEffects.find((e: { id: string; name: string; duration?: string; description?: string }) => e.id === eff.id)!;
        const changes: Record<string, unknown> = {};
        if (eff.name !== undefined && eff.name !== currentEff.name) changes.name = eff.name;
        if (eff.duration !== undefined && eff.duration !== currentEff.duration) changes.duration = eff.duration;
        if (eff.description !== undefined && eff.description !== currentEff.description) changes.description = eff.description;
        if (Object.keys(changes).length > 0) {
          commands.push({ type: 'sheet:effect-update', sheetId, effectId: eff.id, changes });
        }
      }
    }
  }

  // 7.5 Resources
  if (Array.isArray(diff.resources)) {
    handledKeys.add('resources');
    const currentResources = (currentData.resources ?? []) as HudResource[];
    const nextResources = diff.resources as HudResource[];
    const currentIds = new Set(currentResources.map((r: HudResource) => r.id));
    const nextIds = new Set(nextResources.map((r: HudResource) => r.id));

    for (const res of currentResources) {
      if (!nextIds.has(res.id)) {
        commands.push({ type: 'sheet:resource-remove', sheetId, resourceId: res.id });
      }
    }

    for (const res of nextResources) {
      if (!currentIds.has(res.id)) {
        commands.push({
          type: 'sheet:resource-add',
          sheetId,
          resource: {
            name: res.name,
            current: res.current,
            max: res.max,
            recharge: res.recharge,
          },
        });
      } else {
        const currentRes = currentResources.find((r: HudResource) => r.id === res.id)!;
        const changes: Record<string, unknown> = {};
        if (res.name !== currentRes.name) changes.name = res.name;
        if (res.current !== currentRes.current) changes.current = res.current;
        if (res.max !== currentRes.max) changes.max = res.max;
        if (res.recharge !== currentRes.recharge) changes.recharge = res.recharge;
        if (Object.keys(changes).length > 0) {
          commands.push({ type: 'sheet:resource-edit', sheetId, resourceId: res.id, changes });
        }
      }
    }
  }

  // 8. Attacks
  // 2026-06-02: bug fix — было `handledKeys.add('combat')` что блокировало
  // generic-fallback для combat.initiative / combat.round / combat.notes когда
  // batched diff содержал и attacks и эти поля. Заменено на narrower
  // handledPaths.add('combat.attacks') — attacks array всё равно skips generic
  // fallback (нет WRITE_KEY_MAP entry для combat.attacks), но другие combat.*
  // paths теперь проходят.
  if (diff.combat?.attacks) {
    handledPaths.add('combat.attacks');
    const diffAttacks = diff.combat.attacks as Array<{ id: string; name: string; attackBonus: number; damage: string; damageType: string; meta?: string }>;
    const currentIds = new Set(currentData.combat.attacks.map((a: { id: string }) => a.id));
    const diffIds = new Set(diffAttacks.map((a: { id: string }) => a.id));

    for (const atk of diffAttacks) {
      if (!currentIds.has(atk.id)) {
        commands.push({
          type: 'sheet:repeating-row-add',
          sheetId,
          sectionKey: 'attacks',
          row: {
            rowId: atk.id,
            values: {
              name: atk.name,
              to_hit: atk.attackBonus,
              damage_dice: atk.damage,
              damage_type: atk.damageType,
              properties: atk.meta ?? '',
            },
          },
        });
      }
    }

    for (const atk of currentData.combat.attacks) {
      if (!diffIds.has(atk.id)) {
        commands.push({ type: 'sheet:repeating-row-remove', sheetId, sectionKey: 'attacks', rowId: atk.id });
      }
    }

    for (const atk of diffAttacks) {
      if (currentIds.has(atk.id)) {
        const currentAtk = currentData.combat.attacks.find((a: { id: string; name: string; attackBonus: number; damage: string; damageType: string }) => a.id === atk.id)!;
        const changes: Record<string, unknown> = {};
        if (atk.name !== currentAtk.name) changes.name = atk.name;
        if (atk.attackBonus !== currentAtk.attackBonus) changes.to_hit = atk.attackBonus;
        if (atk.damage !== currentAtk.damage) changes.damage_dice = atk.damage;
        if (atk.damageType !== currentAtk.damageType) changes.damage_type = atk.damageType;
        if (Object.keys(changes).length > 0) {
          commands.push({ type: 'sheet:repeating-row-update', sheetId, sectionKey: 'attacks', rowId: atk.id, changes });
        }
      }
    }
  }

  // 9. Inventory
  if (diff.inventory) {
    handledKeys.add('inventory');
    const diffItems = diff.inventory as Array<{ id: string; name: string; quantity: number; weight?: number; equipped?: boolean; description?: string; item_type?: string; srd_slug?: string; weapon?: boolean; base_ac?: number; armor_category?: string; attuned?: boolean }>;
    const currentIds = new Set(currentData.inventory.map((i: { id: string }) => i.id));
    const diffIds = new Set(diffItems.map((i: { id: string }) => i.id));

    for (const item of diffItems) {
      if (!currentIds.has(item.id)) {
        commands.push({
          type: 'sheet:repeating-row-add',
          sheetId,
          sectionKey: 'inventory',
          row: {
            rowId: item.id,
            values: {
              name: item.name,
              quantity: item.quantity,
              weight: item.weight ?? 0,
              description: item.description ?? '',
              equipped: item.equipped ?? false,
              item_type: item.item_type ?? 'gear',
              srd_slug: item.srd_slug ?? '',
              base_ac: item.base_ac ?? 0,
              armor_category: item.armor_category ?? 'Light',
              attuned: item.attuned ?? false,
            },
          },
        });
      }
    }

    for (const item of currentData.inventory) {
      if (!diffIds.has(item.id)) {
        commands.push({ type: 'sheet:repeating-row-remove', sheetId, sectionKey: 'inventory', rowId: item.id });
      }
    }

    for (const item of diffItems) {
      if (currentIds.has(item.id)) {
        const currentItem = currentData.inventory.find((i: { id: string; name: string; quantity: number; weight?: number; equipped?: boolean; description?: string; item_type?: string; srd_slug?: string; base_ac?: number; armor_category?: string; attuned?: boolean }) => i.id === item.id)!;
        const changes: Record<string, unknown> = {};
        if (item.name !== currentItem.name) changes.name = item.name;
        if (item.quantity !== currentItem.quantity) changes.quantity = item.quantity;
        if (item.weight !== undefined && item.weight !== currentItem.weight) changes.weight = item.weight;
        if (item.equipped !== undefined && item.equipped !== currentItem.equipped) changes.equipped = item.equipped;
        if (item.description !== undefined && item.description !== currentItem.description) changes.description = item.description;
        if (item.item_type !== undefined && item.item_type !== currentItem.item_type) changes.item_type = item.item_type;
        if (item.srd_slug !== undefined && item.srd_slug !== currentItem.srd_slug) changes.srd_slug = item.srd_slug;
        if (item.base_ac !== undefined && item.base_ac !== currentItem.base_ac) changes.base_ac = item.base_ac;
        if (item.armor_category !== undefined && item.armor_category !== currentItem.armor_category) changes.armor_category = item.armor_category;
        if (item.attuned !== undefined && item.attuned !== currentItem.attuned) changes.attuned = item.attuned;
        if (Object.keys(changes).length > 0) {
          commands.push({ type: 'sheet:repeating-row-update', sheetId, sectionKey: 'inventory', rowId: item.id, changes });
        }
      }
    }
  }

  // 10b. Portrait — dedicated null-aware command (must precede the generic
  // fallback so identity.portrait does not get swept into sheet:update if a
  // future WRITE_KEY_MAP entry is added for it).
  if (diff.identity?.portrait !== undefined) {
    commands.push({
      type: 'sheet:set-portrait',
      sheetId,
      portrait: diff.identity.portrait as string | null,
    });
    handledPaths.add('identity.portrait');
  }

  // 10c. Action economy (2026-06-01) — template stores combined as JSON string
  // в `values.action_economy`, не три separate keys. Build merged object from
  // currentData + diff, emit single sheet:update с JSON-encoded payload.
  // Paths marked handled чтобы generic fallback не emit duplicate keys.
  const incomingAE = diff.combat?.actionEconomy as Partial<{ action: boolean; bonus: boolean; reaction: boolean }> | undefined;
  if (incomingAE && (incomingAE.action !== undefined || incomingAE.bonus !== undefined || incomingAE.reaction !== undefined)) {
    const current = currentData.combat?.actionEconomy ?? { action: true, bonus: true, reaction: true };
    const merged = { ...current, ...incomingAE };
    commands.push({
      type: 'sheet:update',
      sheetId,
      changes: { action_economy: JSON.stringify(merged) },
    });
    handledPaths.add('combat.actionEconomy.action');
    handledPaths.add('combat.actionEconomy.bonus');
    handledPaths.add('combat.actionEconomy.reaction');
  }

  // 9d. Roleplay (rp) — granular commands. The full-sheet overlay routes ALL
  // rp edits through toCommands; without this branch the patch produced no
  // command, the optimistic overlay never confirmed, and the user saw
  // "server didn't respond" + lost the edit. Server handlers (sheet-rp.ts) and
  // client echo consumption already exist; this is the missing write bridge.
  if (diff.rp) {
    handledKeys.add('rp');
    const drp = diff.rp as Record<string, unknown>;
    const crp = (currentData.rp ?? {}) as Record<string, unknown>;

    // Scalar fields
    const RP_FIELDS = ['ideal', 'bond', 'flaw', 'backstory', 'manners'] as const;
    for (const field of RP_FIELDS) {
      const nv = drp[field];
      if (typeof nv === 'string' && nv !== crp[field]) {
        commands.push({ type: 'sheet:rp-field-update', sheetId, field, value: nv });
      }
    }

    // Board config — send the MERGED full config (server replaces it wholesale).
    if (drp.boardConfig !== undefined) {
      const merged = { ...(crp.boardConfig as Record<string, unknown>), ...(drp.boardConfig as Record<string, unknown>) };
      const cmd = toBoardConfigUpdateCommand(sheetId, merged as BoardConfigPayload);
      if (cmd) commands.push(cmd);
    }

    // List fields — diff by id → add / update / remove.
    const RP_LISTS = ['goals', 'npcs', 'factions', 'sessionLog', 'moments'] as const;
    for (const listKey of RP_LISTS) {
      const nextRaw = drp[listKey];
      if (!Array.isArray(nextRaw)) continue;
      const next = nextRaw as Array<Record<string, unknown> & { id?: string }>;
      const cur = (Array.isArray(crp[listKey]) ? crp[listKey] : []) as Array<Record<string, unknown> & { id?: string }>;
      const curById = new Map(cur.filter(i => typeof i.id === 'string').map(i => [i.id as string, i]));
      const nextIds = new Set(next.map(i => i.id).filter((x): x is string => typeof x === 'string'));
      for (const item of next) {
        if (typeof item.id !== 'string') continue;
        const existing = curById.get(item.id);
        if (!existing) {
          commands.push({ type: 'sheet:rp-list-add', sheetId, listKey, item });
        } else if (JSON.stringify(existing) !== JSON.stringify(item)) {
          commands.push({ type: 'sheet:rp-list-update', sheetId, listKey, itemId: item.id, changes: item });
        }
      }
      for (const item of cur) {
        if (typeof item.id === 'string' && !nextIds.has(item.id)) {
          commands.push({ type: 'sheet:rp-list-remove', sheetId, listKey, itemId: item.id });
        }
      }
    }
  }

  // 10. Generic fallback
  // Saves (saves.* → *_save_prof) and skills (skills.* → *_prof) carry a
  // numeric profLevel tier (0|1|2) per ../sheet-values-schema.ts; they flow
  // through here as raw `val` with NO boolean coercion, so expertise(2)
  // reaches the wire intact (T001 contract — write-side passthrough).
  const genericChanges: Record<string, unknown> = {};
  const flatEntries = flattenPaths(diff as Record<string, unknown>);

  for (const [path, val] of flatEntries) {
    const topKey = path.split('.')[0];
    if (handledKeys.has(topKey)) continue;
    if (handledPaths.has(path)) continue;
    const serverKey = WRITE_KEY_MAP[path];
    if (!serverKey) continue;
    if (typeof val === 'object' && val !== null && !Array.isArray(val)) continue;
    genericChanges[serverKey] = val;
  }

  if (Object.keys(genericChanges).length > 0) {
    commands.push({ type: 'sheet:update', sheetId, changes: genericChanges });
  }

  return commands;
}

// ─── Board Config Update Route (T102 — MEGA² E13) ───────────
// Single chokepoint for sheet:rp-board-config-update commands. Validates
// that the proposed HudBoardConfig.cards still fit the 12-column grid
// before letting the command reach the wire; invalid payloads are
// rejected (returns null) so the caller can surface a warning instead
// of broadcasting a malformed layout that would shift on the recipients.
//
// `validateBoardConfig` below is a local mirror of the helper in
// src/components/character-hud/character-mode/types.ts (shared/ cannot
// import src/). Keep both in sync when the layout invariants change.

export const BOARD_GRID_COLS = 12;

export type BoardCardConfig = { id: string; span: number; order: number };
export type BoardConfigPayload = {
  cards: BoardCardConfig[];
  autoLayout?: boolean;
  [k: string]: unknown;
};

export type BoardConfigUpdateCommand = {
  type: 'sheet:rp-board-config-update';
  sheetId: string;
  config: BoardConfigPayload;
};

export function validateBoardConfig(cards: BoardCardConfig[]): true | string[] {
  if (!Array.isArray(cards)) {
    return ['boardConfig.cards must be an array'];
  }

  const errors: string[] = [];
  const sorted = [...cards].sort((a: BoardCardConfig, b: BoardCardConfig) => a.order - b.order);
  let rowSum = 0;
  let rowIdx = 0;

  for (const card of sorted) {
    if (typeof card.span !== 'number' || card.span < 1 || card.span > BOARD_GRID_COLS) {
      errors.push(`Card "${card.id}" has invalid span ${String(card.span)} (expected 1..${BOARD_GRID_COLS}).`);
      continue;
    }
    if (rowSum + card.span > BOARD_GRID_COLS) {
      rowIdx++;
      rowSum = card.span;
    } else {
      rowSum += card.span;
    }
    if (rowSum > BOARD_GRID_COLS) {
      errors.push(`Row ${rowIdx} sum exceeds ${BOARD_GRID_COLS} after card "${card.id}".`);
    }
  }

  return errors.length === 0 ? true : errors;
}

/**
 * Convert an outgoing HudBoardConfig update into a wire command, after
 * validating the proposed layout. Returns `null` when validation fails —
 * caller decides whether to surface a toast, log, or silently drop.
 */
export function toBoardConfigUpdateCommand(
  sheetId: string,
  config: BoardConfigPayload,
): BoardConfigUpdateCommand | null {
  const cards = Array.isArray(config?.cards) ? (config.cards as BoardCardConfig[]) : [];
  const result = validateBoardConfig(cards);
  if (result !== true) {
    return null;
  }
  return { type: 'sheet:rp-board-config-update', sheetId, config };
}
